import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCcw, LogOut, Crown, ChevronsUpDown, Building2 } from 'lucide-react'
import { toast } from 'sonner'
import { toastError } from '@/lib/errores'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { formatNivel } from '@/lib/roles'

/**
 * SelectCoPage — selector de organización (CO) al iniciar sesión.
 *
 * Orden de mejora 2026-05-25 (AARPIA.infra.orden-mejora.aplicada): las tarjetas
 * fijas se sustituyen por un combobox con buscador que filtra en vivo según se
 * escribe. Muestra TODOS los COs sobre los que el usuario tiene cualquier vínculo
 * activo (fuente: /mis-context-owners → actor-contextowner.vinculado, cualquier nivel).
 * Escala a decenas de COs sin saturar la pantalla.
 */
export function SelectCoPage() {
  const contextOwners = useAuthStore((s) => s.contextOwners)
  const selectCo = useAuthStore((s) => s.selectCo)
  const logout = useAuthStore((s) => s.logout)
  const loadMyContextOwners = useAuthStore((s) => s.loadMyContextOwners)
  const loading = useAuthStore((s) => s.loading)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  // Refrescar SIEMPRE al montar, no solo si la lista está vacía: tras dar de
  // alta un cliente nuevo (que crea un CO) la lista en memoria está stale y
  // no-vacía, así que el guard `length === 0` ocultaba el CO recién creado.
  // Llegar a /select-co es una navegación deliberada → el refetch es barato.
  useEffect(() => {
    loadMyContextOwners()
  }, [loadMyContextOwners])

  async function handleSelect(coId: string, nombre: string) {
    setOpen(false)
    try {
      await selectCo(coId)
      toast.success(`Operando como ${nombre}`)
      navigate('/app', { replace: true })
    } catch (e: any) {
      toastError(e, 'No se pudo cambiar de organización')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Elige una organización</CardTitle>
          <CardDescription>
            Selecciona la organización con la que vas a trabajar en esta sesión.
            Escribe para buscar entre las que tienes acceso. Podrás cambiar más tarde
            desde el menú lateral.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {contextOwners.length === 0 ? (
            loading ? (
              <div className="space-y-2">
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No tienes acceso a ninguna organización.
              </div>
            )
          ) : (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={open}
                  aria-haspopup="listbox"
                  aria-controls="select-co-listbox"
                  aria-label="Elegir organización"
                  disabled={loading}
                  className="flex h-12 w-full items-center justify-between rounded-lg bg-card px-4 text-left text-sm shadow-md transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  <span className="flex items-center gap-2.5 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    Selecciona o busca una organización…
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg"
              >
                <Command
                  filter={(value, search) =>
                    value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                  }
                >
                  <CommandInput placeholder="Buscar organización…" />
                  <CommandList id="select-co-listbox">
                    <CommandEmpty>No se encontró ninguna organización.</CommandEmpty>
                    <CommandGroup>
                      {contextOwners.map((co) => (
                        <CommandItem
                          key={co.id}
                          value={`${co.nombre} ${co.nivel ?? ''}`}
                          onSelect={() => handleSelect(co.id, co.nombre)}
                          // `group` + el subtexto reacciona a data-[selected]: sin esto el
                          // subtexto muted queda gris sobre el cian del item resaltado (DC-5d
                          // contraste). Regla, no parche: cualquier item con subtexto la hereda.
                          className="group py-2.5"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Building2 className="h-4 w-4" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 font-medium">
                              <span className="truncate">{co.nombre}</span>
                              {co.es_propietario && <Crown className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                            </div>
                            {/* DC-5d: el nivel YA dice "Propietario" cuando lo es (formatNivel) y el
                                Crown lo marca visualmente — el sufijo duplicaba "Propietario · Propietario".
                                Mostrar solo el nivel; añadir "· Miembro" solo cuando NO es propietario. */}
                            <div className="text-xs text-muted-foreground group-data-[selected=true]:text-accent-foreground/80">
                              {formatNivel(co.nivel)}{co.es_propietario ? '' : ' · Miembro'}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </CardContent>
        <div className="px-6 pb-6 pt-2 flex justify-between text-sm">
          <Button variant="ghost" size="sm" onClick={() => { logout(); toast.success('Sesión cerrada'); navigate('/login') }}>
            <LogOut className="h-4 w-4" />
            Salir
          </Button>
          <Button variant="ghost" size="sm" onClick={loadMyContextOwners} disabled={loading} aria-label="Recargar organizaciones">
            <RefreshCcw className="h-4 w-4" />
            Recargar
          </Button>
        </div>
      </Card>
    </div>
  )
}
